import { emptyGlossary, referencedTermIds, synonymClustersFrom, type Glossary } from "./glossary";
import { reviewBriefing, summaryItems, summaryItemText, type ReviewGuide } from "./guide";

export interface CopyQualityDiagnostic {
  severity: "warning" | "info";
  code: string;
  message: string;
}

const reviewTaskTitle = /^(?:check|confirm|ensure|inspect|look at|open|prove|review|trace|validate|verify)\b/i;
const abstractLayerTitle = /^(?:add|create|define|expose|introduce|provide|support|wire)\b.*\b(?:api surface|surface|path|flow|work lists?|wiring|plumbing)\s*[.!:]?$/i;
const emptyPhrases = [
  /\bprove the seams?\b/i,
  /\bopen the .{0,36}\bpath\b/i,
  /\bmake sure\b/i,
  /\bworks? as expected\b/i,
  /\bhandle(?:s|d)? (?:it|things?|this) correctly\b/i,
  /\bvarious (?:changes?|things?|parts?)\b/i,
  /\brobust(?:ly|ness)?\b/i,
];
const technicalOrientationTerms = /\b(?:API|DTO|gRPC|HMAC|protobuf|controller|datalayer|dependency injection|DI registration|EF migration)\b/i;
const leadingAnaphora = /^(that|this|these|those|it|they|such|the former|the latter)\b(?!\s+(?:pull request|PR|change|guide))/i;

/**
 * Fallback vocabulary for repositories with no glossary yet. A glossary supersedes this,
 * because a guessed cluster is worth much less than the words a team actually uses.
 */
const fallbackSynonymClusters: readonly (readonly string[])[] = [
  ["cadence", "rhythm", "tempo"],
  ["retry", "recheck", "reattempt"],
  ["endpoint", "route", "entrypoint"],
  ["flag", "toggle", "switch"],
];


/** Crude but sufficient: collapse inflections so "probe", "probes" and "probing" agree. */
function stemWord(word: string): string {
  const bare = word.toLowerCase().replace(/[^a-z]/g, "");
  return bare.replace(/(ing|ed|es|s)$/, "").replace(/e$/, "");
}

function stemPhrase(phrase: string): string[] {
  return phrase.split(/\s+/).map(stemWord).filter(Boolean);
}

/**
 * A term reference is the canonical noun, not prose. Its href carries the term id — a slug that
 * repeats the term's own words — so leaving it in the text made referencing the glossary trip the
 * very check the glossary powers.
 */
function withoutTermHrefs(value: string): string {
  return value.replace(/\]\(chew:\/\/term\/[a-z0-9-]+\)/g, "]");
}

/**
 * An aka list mixes genuine alternatives ("reachability check") with inflections and sub-phrases
 * of the term itself ("probe", "probing" under "Health probing"). Grouping members into families
 * of shared word stems separates the two: everything built on the same root is one way of saying
 * the concept, and only a second family is evidence the author switched nouns.
 *
 * Filtering members instead of grouping them was wrong in both directions — two members with
 * identical stems survived and made the check unsatisfiable, and a short member folded into a
 * longer one stopped counting even when the longer phrase never appeared.
 */
interface ClusterFamily {
  labels: string[];
  phrases: string[][];
  stems: Set<string>;
}

function clusterFamilies(cluster: readonly string[]): ClusterFamily[] {
  const families: ClusterFamily[] = [];
  for (const label of cluster) {
    const phrase = stemPhrase(label);
    if (phrase.length === 0) continue;
    families.push({ labels: [label], phrases: [phrase], stems: new Set(phrase) });
  }

  // Merge until stable: a later member can bridge two families that looked unrelated.
  for (let merged = true; merged; ) {
    merged = false;
    outer: for (let left = 0; left < families.length; left += 1) {
      for (let right = left + 1; right < families.length; right += 1) {
        const shares = [...families[right]!.stems].some((stem) => families[left]!.stems.has(stem));
        if (!shares) continue;
        families[left]!.labels.push(...families[right]!.labels);
        families[left]!.phrases.push(...families[right]!.phrases);
        for (const stem of families[right]!.stems) families[left]!.stems.add(stem);
        families.splice(right, 1);
        merged = true;
        break outer;
      }
    }
  }
  return families;
}

function containsPhrase(haystack: string[], phrase: string[]): boolean {
  if (phrase.length === 0) return false;
  return haystack.some((_, index) => phrase.every((stem, offset) => haystack[index + offset] === stem));
}

function words(value: string): number {
  return value.trim().split(/\s+/).filter(Boolean).length;
}

/** Bold spans, ignoring inline code where ** would not render as emphasis anyway. */
function emphasisSpans(value: string): string[] {
  const withoutCode = value.replace(/`[^`]*`/g, "");
  return [...withoutCode.matchAll(/\*\*([^*]+)\*\*/g)].map((match) => match[1]!.trim());
}

/**
 * Emphasis anchors a skim on the load-bearing clause. Past roughly one span per paragraph it
 * stops anchoring anything, and a long bolded run is just a second voice for the whole sentence.
 */
function checkEmphasis(diagnostics: CopyQualityDiagnostic[], field: string, value: string | undefined): void {
  if (!value) return;
  const spans = emphasisSpans(value);
  if (spans.length === 0) return;

  const paragraphs = Math.max(1, value.split(/\n{2,}/).filter((block) => block.trim()).length);
  if (spans.length > paragraphs) {
    diagnostics.push({
      severity: "warning",
      code: "COPY_EMPHASIS_OVERUSED",
      message: `${field} bolds ${spans.length} spans across ${paragraphs} paragraph(s). Emphasis everywhere anchors nothing — keep it to the one clause carrying the claim, and split the paragraph if two clauses deserve it.`,
    });
  }

  const sprawling = spans.find((span) => words(span) > 12);
  if (sprawling) {
    diagnostics.push({
      severity: "warning",
      code: "COPY_EMPHASIS_SPRAWL",
      message: `${field} bolds a ${words(sprawling)}-word span ('${sprawling.slice(0, 48)}…'). Emphasise the clause that carries the claim, not the sentence around it.`,
    });
  }
}


function sentences(value: string): string[] {
  return value.trim().split(/(?<=[.!?])\s+/).filter((sentence) => sentence.trim().length > 0);
}

function standardDeviation(values: number[]): number {
  if (values.length < 2) return 0;
  const mean = values.reduce((total, value) => total + value, 0) / values.length;
  return Math.sqrt(values.reduce((total, value) => total + (value - mean) ** 2, 0) / values.length);
}

/**
 * Prose rhythm. Generated copy drifts toward one sentence shape repeated — every sentence the
 * same length, or one sentence built from three clauses of identical weight — which reads as a
 * spec sheet rather than an explanation and is tiring well before it is inaccurate.
 */
function checkRhythm(diagnostics: CopyQualityDiagnostic[], field: string, value: string | undefined): void {
  if (!value) return;
  const parts = sentences(value);
  const lengths = parts.map((sentence) => words(sentence));

  if (lengths.length >= 4 && standardDeviation(lengths) < 3.5) {
    diagnostics.push({
      severity: "warning",
      code: "COPY_MONOTONOUS_RHYTHM",
      message: `${field} runs ${lengths.length} sentences of near-identical length (${lengths.join(", ")} words). Vary them — join two, or cut one to a short pivot — so the copy reads rather than recites.`,
    });
  }

  for (const sentence of parts) {
    const clauses = sentence.split(/[;,]/).map((clause) => clause.trim()).filter(Boolean);
    if (clauses.length < 3) continue;
    const clauseLengths = clauses.map((clause) => words(clause));
    if (Math.max(...clauseLengths) - Math.min(...clauseLengths) <= 4) {
      diagnostics.push({
        severity: "warning",
        code: "COPY_STACCATO_CLAUSES",
        message: `${field} builds a sentence from ${clauses.length} clauses of the same weight (${clauseLengths.join(", ")} words): '${sentence.slice(0, 64)}…'. Three parallel clauses read as a list crammed into a sentence; recast or split it.`,
      });
      break;
    }
  }
}

function checkEmptyPhrase(
  diagnostics: CopyQualityDiagnostic[],
  field: string,
  value: string | undefined,
): void {
  if (!value) return;
  const phrase = emptyPhrases.find((candidate) => candidate.test(value));
  if (phrase) {
    diagnostics.push({
      severity: "warning",
      code: "COPY_EMPTY_PHRASE",
      message: `${field} contains generic review language instead of PR-specific information: '${value}'.`,
    });
  }
}

/**
 * Catches objective copy smells. Meaning and domain accuracy remain the authoring
 * agent's job; strict validation turns these warnings into a release gate.
 */
export function assessChewfileCopy(guide: ReviewGuide, glossary: Glossary = emptyGlossary): CopyQualityDiagnostic[] {
  const diagnostics: CopyQualityDiagnostic[] = [];
  const orientation = guide.overview.orientation;
  const briefing = reviewBriefing(guide);

  const presentedBeforeCode: Array<[string, string]> = (() => {
    if (briefing === "none") return [];
    if (briefing === "brief") {
      return [
        ["overview.title", guide.overview.title],
        ["overview.lead", guide.overview.lead ?? guide.overview.problem ?? guide.overview.approach ?? orientation?.definition ?? ""],
        ["overview.summary", summaryItems(guide).slice(0, 3).map(summaryItemText).join(" ")],
        ["overview.mainRisk", guide.overview.mainRisk],
      ];
    }
    const shared: Array<[string, string]> = [
      ["overview.title", guide.overview.title],
      ["overview.lead", guide.overview.lead ?? ""],
      ["overview.orientation.definition", orientation?.definition ?? ""],
      ["overview.orientation.purpose", orientation?.purpose ?? ""],
      ["overview.problem", guide.overview.problem ?? ""],
      ["overview.approach", guide.overview.approach ?? ""],
      ["overview.summary", summaryItems(guide).map(summaryItemText).join(" ")],
    ];
    if (briefing === "standard") return [...shared, ["overview.mainRisk", guide.overview.mainRisk]];
    return [
      ...shared,
      ["overview.today", guide.overview.today ?? ""],
      ["overview.after", guide.overview.after ?? ""],
      ["overview.reviewFocus", guide.overview.reviewFocus.join(" ")],
      ["overview.mainRisk", guide.overview.mainRisk],
    ];
  })();
  const presented = presentedBeforeCode.filter(([, value]) => value.trim().length > 0);
  const presentedWordCount = presented.reduce((total, [, value]) => total + words(value), 0);
  const wordBudget = { none: 0, brief: 120, standard: 250, full: 500 }[briefing];
  if (presentedWordCount > wordBudget) {
    // Name the counted fields and the heaviest ones. Without that the cheapest way to make
    // budget is to delete specifics and keep generalisations, which is the wrong edit.
    const heaviest = [...presented]
      .sort((left, right) => words(right[1]) - words(left[1]))
      .slice(0, 3)
      .map(([field, value]) => `${field} (${words(value)}w)`)
      .join(", ");
    diagnostics.push({
      severity: "warning",
      code: "COPY_BRIEF_TOO_LONG",
      message: `${briefing} pre-code briefing presents about ${presentedWordCount} words across ${presented.length} counted fields; keep it below ${wordBudget}. Heaviest: ${heaviest}. Cut hedges, restated motivation, or drop a whole field — never a number, a name or a concrete trace. If the specifics genuinely will not fit, scope.breadth/novelty is probably understated.`,
    });
  }

  if (briefing !== "none" && !guide.overview.lead) {
    diagnostics.push({
      severity: "warning",
      code: "COPY_MISSING_LEAD",
      message: "overview.lead is missing, so the briefing opens with the product-concept card instead of what this PR does. Write one or two sentences for a reader who reads nothing else.",
    });
  }

  if (orientation) {
    if (words(orientation.definition) < 10) {
      diagnostics.push({
        severity: "warning",
        code: "COPY_THIN_DEFINITION",
        message: "overview.orientation.definition must explain the subject to a cold reader, not merely rename it.",
      });
    }
    if (words(orientation.purpose) < 7) {
      diagnostics.push({
        severity: "warning",
        code: "COPY_THIN_PURPOSE",
        message: "overview.orientation.purpose must say why the subject exists or who benefits from it.",
      });
    }
    if (technicalOrientationTerms.test(orientation.definition)) {
      diagnostics.push({
        severity: "warning",
        code: "COPY_TECHNICAL_ORIENTATION",
        message: "overview.orientation.definition starts the review with implementation vocabulary; define the product concept in plain terms first.",
      });
    }
  }

  // Each pre-code field renders in its own box and may be read alone or out of order, so a
  // demonstrative pointing at a neighbouring field is a broken reference for the reader.
  for (const [field, value] of presented) {
    if (field === "overview.title" || field === "overview.summary") continue;
    const opener = leadingAnaphora.exec(value.trimStart());
    if (opener) {
      diagnostics.push({
        severity: "warning",
        code: "COPY_CROSS_FIELD_ANAPHORA",
        message: `${field} opens with '${opener[1]}', which points at a different field. Each field is read standalone — open with the concrete noun instead.`,
      });
    }
  }

  // A model reaching for prose variety produces synonym drift automatically, and every
  // synonym makes the reader re-derive that two words mean one thing.
  const briefingStems = stemPhrase(withoutTermHrefs(presented.map(([, value]) => value).join(" ")));
  const glossaryClusters = synonymClustersFrom(glossary);
  for (const cluster of (glossaryClusters.length > 0 ? glossaryClusters : fallbackSynonymClusters)) {
    const used = clusterFamilies(cluster)
      .filter((family) => family.phrases.some((phrase) => containsPhrase(briefingStems, phrase)))
      // The longest label reads as the canonical name of the family.
      .map((family) => [...family.labels].sort((a, b) => b.length - a.length)[0]!);
    if (used.length > 1) {
      diagnostics.push({
        severity: "warning",
        code: "COPY_SYNONYM_DRIFT",
        message: `the briefing uses ${used.map((term) => `'${term}'`).join(" and ")} for what is probably one concept; pick the noun the orientation established and reuse it.`,
      });
    }
  }

  const overviewFields: Array<[string, string | undefined]> = [
    ["overview.title", guide.overview.title],
    ["overview.lead", guide.overview.lead],
    ["overview.problem", guide.overview.problem],
    ["overview.approach", guide.overview.approach],
    ["overview.mainRisk", guide.overview.mainRisk],
    ...summaryItems(guide).map((item, index) => [`overview.summary[${index}]`, summaryItemText(item)] as [string, string]),
    ...guide.overview.reviewFocus.map((value, index) => [`overview.reviewFocus[${index}]`, value] as [string, string]),
  ];
  for (const [field, value] of overviewFields) checkEmptyPhrase(diagnostics, field, value);

  // Titles and summary bullets are already short and prominent; bolding inside them is noise.
  for (const [field, value] of [
    ["overview.title", guide.overview.title] as const,
    ...summaryItems(guide).map((item, index) => [`overview.summary[${index}]`, summaryItemText(item)] as const),
    ...guide.layers.map((layer) => [`layers.${layer.id}.title`, layer.title] as const),
  ]) {
    if (emphasisSpans(value).length > 0) {
      diagnostics.push({
        severity: "warning",
        code: "COPY_EMPHASIS_MISPLACED",
        message: `${field} contains bold text. Titles and summary bullets are already prominent — drop the emphasis and let the surrounding prose carry it.`,
      });
    }
  }
  // A term reference that resolves to nothing renders as plain text, so the reader loses the
  // definition silently. Surface it to the author instead.
  const allCopy = [
    ...presented.map(([, value]) => value),
    ...guide.layers.map((layer) => layer.narrative),
  ];
  const knownTermIds = new Set(glossary.terms.map((term) => term.id));
  for (const id of referencedTermIds(allCopy)) {
    if (!knownTermIds.has(id)) {
      diagnostics.push({
        severity: "warning",
        code: "COPY_UNKNOWN_TERM",
        message: `guide copy references glossary term '${id}', which neither the central glossary nor the repository overlay defines. Add the term or drop the link.`,
      });
    }
  }

  // Redefining a term the repository already owns is how one concept ends up with several
  // slightly different definitions, which is the drift a glossary exists to stop.
  const briefingProse = presented
    .filter(([field]) => field !== "overview.title")
    .map(([, value]) => value)
    .join(" ")
    .toLowerCase();
  for (const term of glossary.terms) {
    const referenced = referencedTermIds(allCopy).has(term.id);
    const named = new RegExp(`\\b${term.term.toLowerCase().replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`).test(briefingProse);
    if (named && !referenced && briefingProse.includes(term.definition.slice(0, 24).toLowerCase())) {
      diagnostics.push({
        severity: "warning",
        code: "COPY_REDEFINES_TERM",
        message: `the briefing restates the glossary definition of '${term.term}'. Reference it with [${term.term.toLowerCase()}](chew://term/${term.id}) and spend the words on this PR instead.`,
      });
    }
  }

  checkEmphasis(diagnostics, "overview.lead", guide.overview.lead);
  checkEmphasis(diagnostics, "overview.problem", guide.overview.problem);
  checkEmphasis(diagnostics, "overview.approach", guide.overview.approach);
  checkEmphasis(diagnostics, "overview.mainRisk", guide.overview.mainRisk);
  for (const [field, value] of [["overview.lead", guide.overview.lead], ["overview.problem", guide.overview.problem], ["overview.approach", guide.overview.approach]] as const) {
    checkRhythm(diagnostics, field, value);
  }

  for (const layer of guide.layers) {
    if (reviewTaskTitle.test(layer.title)) {
      diagnostics.push({
        severity: "warning",
        code: "COPY_TASK_TITLE",
        message: `layers.${layer.id}.title tells the reviewer what to check instead of naming what the PR does: '${layer.title}'.`,
      });
    }
    if (abstractLayerTitle.test(layer.title)) {
      diagnostics.push({
        severity: "warning",
        code: "COPY_ABSTRACT_LAYER_TITLE",
        message: `layers.${layer.id}.title describes a container instead of the changed behaviour: '${layer.title}'. Name the actor, result, ordering or boundary the reviewer will find in this layer.`,
      });
    }
    if (words(layer.title) < 4 && guide.scope.breadth !== "tweak") {
      diagnostics.push({
        severity: "warning",
        code: "COPY_THIN_LAYER_TITLE",
        message: `layers.${layer.id}.title is too terse to carry the implementation story: '${layer.title}'.`,
      });
    }
    if (words(layer.narrative) > 110) {
      diagnostics.push({
        severity: "warning",
        code: "COPY_NARRATIVE_TOO_LONG",
        message: `layers.${layer.id}.narrative is ${words(layer.narrative)} words. A narrative orients a reviewer who already has the code open; past roughly 110 words it competes with the diff instead of framing it.`,
      });
    }
    if (words(layer.narrative) < 10) {
      diagnostics.push({
        severity: "warning",
        code: "COPY_THIN_LAYER_NARRATIVE",
        message: `layers.${layer.id}.narrative must explain concrete behaviour and its implementation connection.`,
      });
    }
    checkEmptyPhrase(diagnostics, `layers.${layer.id}.title`, layer.title);
    checkEmptyPhrase(diagnostics, `layers.${layer.id}.narrative`, layer.narrative);
    checkEmphasis(diagnostics, `layers.${layer.id}.narrative`, layer.narrative);
    checkRhythm(diagnostics, `layers.${layer.id}.narrative`, layer.narrative);
  }

  return diagnostics;
}
